import { Component, DestroyRef, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle, MatDatepickerToggleIcon } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { TranslocoDirective } from '@jsverse/transloco';
import { LayoutService } from '../../../../../../../@vex/services/layout.service';
import { DateUtil } from '../../../../../../util/DateUtil';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { FlatrateExemptionService } from '../flatrates-exemption.service';
import { Exemption, ExemptionService, Flatrate, FlatrateService, TenantUnitReference } from '@eadsysteme/billingclient-v1';
import { SessionService } from '../../../../../../services/session.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { DecimalFormatDirective } from '../../../../../../directives/decimal-format.directive';
import { SnackbarService } from '../../../../../../services/snackbar.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DayjsDateAdapter, MAT_DAYJS_DATE_ADAPTER_OPTIONS } from '../../../../../../util/material-dayjs-adapter';
import { FlatrateExemptionDetail } from '../FlatrateExemptionDetail';

@Component({
	selector: 'vex-flatrate-exemption-dialog',
	imports: [
		FormsModule,
		MatButton,
		MatDatepicker,
		MatDatepickerInput,
		MatDatepickerToggle,
		MatDatepickerToggleIcon,
		MatDialogActions,
		MatDialogContent,
		MatDialogTitle,
		MatError,
		MatFormField,
		MatIcon,
		MatInput,
		MatLabel,
		MatOption,
		MatSelect,
		MatSuffix,
		ReactiveFormsModule,
		TranslocoDirective,
		CdkDrag,
		CdkDragHandle,
		DecimalFormatDirective,
		MatProgressSpinner,
		MatIconButton,
	],
	templateUrl: './flatrate-exemption-dialog.component.html',
	styleUrl: './flatrate-exemption-dialog.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	providers: [
		{
			provide: DateAdapter,
			useClass: DayjsDateAdapter,
			deps: [MAT_DATE_LOCALE, MAT_DAYJS_DATE_ADAPTER_OPTIONS],
		},
		{ provide: MAT_DATE_FORMATS, useValue: DateUtil.DATE_FORMATS },
	],
})
export class FlatrateExemptionDialogComponent implements OnInit {
	private destroyRef = inject(DestroyRef);
	private dialogRef = inject(MatDialogRef<FlatrateExemptionDialogComponent>);
	private dialogData = inject(MAT_DIALOG_DATA);
	private sessionService = inject(SessionService);
	private flatrateService = inject(FlatrateService);
	private exemptionService = inject(ExemptionService);
	private snackbarService = inject(SnackbarService);
	private dateUtil = inject(DateUtil);
	protected layoutService = inject(LayoutService);
	protected flatrateExemptionService = inject(FlatrateExemptionService);

	protected readonly toMaxDate = DateUtil.maxDate;
	protected readonly toMinDate = DateUtil.minDate;

	private isNew = signal<boolean>(this.dialogData.isNew);
	private billingId = signal<string>(this.dialogData.billingId);
	protected existingObject = signal<FlatrateExemptionDetail>(this.dialogData.objectToEdit);
	private costGroupId = signal<string>(this.dialogData.costGroupId);
	protected tenantUnits = signal<TenantUnitReference[]>(this.dialogData.tenantUnits);
	protected flatrateExemptionFg = new FormGroup<any>({
		tenantUnit: new FormControl({ value: null, disabled: !this.isNew() }, Validators.required),
		validFrom: new FormControl({ value: null, disabled: false }, Validators.required),
		validTo: new FormControl({ value: null, disabled: false }, Validators.required),
	});
	finishedLoading = signal<boolean>(false);

	ngOnInit(): void {
		if (this.flatrateExemptionService.selectedType() === 'FLATRATE') {
			this.flatrateExemptionFg.addControl('value', new FormControl<number>(null, Validators.required));
		}
		if (!this.isNew()) {
			this.fillFormControls();
		}
	}

	private fillFormControls() {
		const tenantUnitsById = new Map(this.tenantUnits().map((tu) => [tu.exportId, tu]));
		this.flatrateExemptionFg.patchValue({
			tenantUnit: tenantUnitsById.get(this.existingObject().tenantUnitReferenceId),
			validFrom: this.existingObject().validFrom,
			validTo: this.existingObject().validTo,
		});
		if (this.flatrateExemptionService.selectedType() === 'FLATRATE') {
			this.flatrateExemptionFg.patchValue({
				value: this.existingObject().value,
			});
		}
	}

	close(saved: boolean) {
		if (!saved) {
			this.dialogRef.close(saved);
			return;
		}

		if (this.flatrateExemptionService.selectedType() === 'FLATRATE') {
			this.createOrUpdateFlatrate();
		} else if (this.flatrateExemptionService.selectedType() === 'EXEMPTION') {
			this.createOrUpdateExemption();
		}
	}

	private createOrUpdateFlatrate() {
		const flatrate: Flatrate = this.mapFormToFlatrate();
		const createOrUpdate$ = this.isNew()
			? this.flatrateService.createFlatrate(this.sessionService.effectiveArea, this.billingId(), this.costGroupId(), flatrate)
			: this.flatrateService.updateFlatrate(this.sessionService.effectiveArea, this.billingId(), this.existingObject().id, flatrate);

		this.handleApiCall(createOrUpdate$);
	}

	private createOrUpdateExemption() {
		const exemption: Exemption = this.mapFormToExemption();
		const createOrUpdate$ = this.isNew()
			? this.exemptionService.createExemption(this.sessionService.effectiveArea, this.billingId(), this.costGroupId(), exemption)
			: this.exemptionService.updateExemption(this.sessionService.effectiveArea, this.billingId(), this.existingObject().id, exemption);

		this.handleApiCall(createOrUpdate$);
	}

	private handleApiCall(apiCall$: Observable<any>) {
		apiCall$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			next: () => this.dialogRef.close(true),
			error: () => {
				this.snackbarService.error('errors.whileSaving');
			},
		});
	}

	private mapFormToFlatrate(): Flatrate {
		return {
			...this.mapFormToBase(),
			value: this.flatrateExemptionFg.getRawValue().value,
		};
	}

	private mapFormToExemption(): Exemption {
		return this.mapFormToBase();
	}

	private mapFormToBase(): Exemption {
		const formValue = this.flatrateExemptionFg.getRawValue();
		return {
			exportId: this.existingObject()?.id ?? null,
			tenantUnitReferenceExportId: formValue.tenantUnit?.exportId,
			validFrom: this.dateUtil.getDateWithoutTimezone(formValue.validFrom).toISOString(),
			validTo: this.dateUtil.getDateWithoutTimezone(formValue.validTo).toISOString(),
		};
	}
}
